import type { UserNotificationSettingsInput } from 'app/generated/gql/graphql'
import { useParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { graphql } from 'app/generated/gql'
import { useQuery } from 'urql'
import { gqlQuery, gqlMutation } from 'app/lib/graphqlClient'
import { opsClient } from 'app/lib/opsUrqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { LiveAlertPreferences, LiveAlertSettings } from 'app/types/live-alerts'
import { useUser } from 'app/utils'
import { withLiveAlertDefaults } from 'lib/liveAlertPreferences'
import { LIVE_ALERT_PREFERENCES_QUERY } from 'app/hooks/useLiveAlertPreferences'
import { logError } from 'lib/logging'

const USER_NOTIFICATION_SETTINGS_QUERY = graphql(`
  query UserNotificationSettings($userId: ID!) {
    userNotificationSettings(userId: $userId) {
      phoneCalls {
        enabled
        canceledAppointment {
          email
          app
        }
        allUnscheduledCalls {
          email
          app
        }
        newPatientUnscheduledCalls {
          email
          app
        }
        callbackRequested {
          email
          app
        }
        missedCalls {
          email
          app
        }
        voicemail {
          email
          app
        }
        incomingCall {
          popup
          sound
        }
      }
      aiAgent {
        enabled
        newAIAgentCall {
          email
          app
        }
        emergencyAppointmentRequest {
          email
          app
        }
        newAppointmentBooked {
          email
          app
        }
      }
      textMessages {
        enabled
        newTextMessage {
          email
          app
          popup
          sound
        }
      }
      formSubmissions {
        enabled
        newFormSubmission {
          email
          app
        }
      }
      scheduler {
        enabled
        newAppointmentBooked {
          email
          app
        }
      }
      pmsData {
        enabled
        noPatientLinkedOnScheduledContact {
          email
          app
        }
        noAppointmentFoundOnScheduledContact {
          email
          app
        }
        brokenAppointment {
          email
          app
        }
      }
      workflowPaused {
        enabled
        email
        app
      }
    }
  }
`)

const UPDATE_USER_NOTIFICATION_SETTINGS_MUTATION = graphql(`
  mutation UpdateUserNotificationSettings($userId: ID!, $input: UserNotificationSettingsInput!) {
    updateUserNotificationSettings(userId: $userId, input: $input) {
      success
    }
  }
`)

// A super admin's own Agent Diagnostics email alerts. They live on the ops schema, so
// the public schema never shows that super users exist, and the API always reads and
// writes the signed-in super admin.
const SUPER_NOTIFICATIONS_QUERY = graphql(`
  query GetSuperNotifications {
    getSuperNotifications {
      enabled
      toolError
      toolTimeout
      toolDuplicate
      noCallsIn48h
      agentChangesEnabled
      agentChangeOffers
      agentChangeServices
      agentChangeBusinessDetails
      agentChangeAppointmentTypes
      agentChangeInsurance
    }
  }
`)

const UPDATE_SUPER_NOTIFICATIONS_MUTATION = graphql(`
  mutation UpdateSuperNotifications($input: SuperNotificationSettingsInput!) {
    updateSuperNotifications(input: $input) {
      enabled
      toolError
      toolTimeout
      toolDuplicate
      noCallsIn48h
      agentChangesEnabled
      agentChangeOffers
      agentChangeServices
      agentChangeBusinessDetails
      agentChangeAppointmentTypes
      agentChangeInsurance
    }
  }
`)

export type AgentDiagnosticsSettings = ResultOf<typeof SUPER_NOTIFICATIONS_QUERY>['getSuperNotifications']

type NotificationChannel = { email?: boolean; app?: boolean; popup?: boolean; sound?: boolean }

export type NotificationSettings = {
  phoneCalls?: {
    enabled?: boolean
    canceledAppointment?: NotificationChannel
    allUnscheduledCalls?: NotificationChannel
    newPatientUnscheduledCalls?: NotificationChannel
    callbackRequested?: NotificationChannel
    missedCalls?: NotificationChannel
    voicemail?: NotificationChannel
    incomingCall?: NotificationChannel
  }
  aiAgent?: {
    enabled?: boolean
    newAIAgentCall?: NotificationChannel
    emergencyAppointmentRequest?: NotificationChannel
    newAppointmentBooked?: NotificationChannel
  }
  textMessages?: {
    enabled?: boolean
    newTextMessage?: NotificationChannel
  }
  formSubmissions?: {
    enabled?: boolean
    newFormSubmission?: NotificationChannel
  }
  scheduler?: {
    enabled?: boolean
    newAppointmentBooked?: NotificationChannel
  }
  pmsData?: {
    enabled?: boolean
    noPatientLinkedOnScheduledContact?: NotificationChannel
    noAppointmentFoundOnScheduledContact?: NotificationChannel
    brokenAppointment?: NotificationChannel
  }
  /** Shaped like the other sections for the form; the API stores it flat (see `withWorkflowPausedDefaults`). */
  workflowPaused?: {
    enabled?: boolean
    workflowPaused?: NotificationChannel
  }
}

type StoredNotificationSettings = Omit<NotificationSettings, 'workflowPaused'> & {
  workflowPaused?: { enabled?: boolean | null; email?: boolean | null; app?: boolean | null } | null
}

type SectionEnabledStates = {
  phoneCalls: boolean
  aiAgent: boolean
  textMessages: boolean
  formSubmissions: boolean
  scheduler: boolean
  pmsData: boolean
  workflowPaused: boolean
}

function buildNotificationInput(
  formData: FormData,
  sectionStates: SectionEnabledStates,
): UserNotificationSettingsInput {
  const channel = (section: string, item: string) => ({
    email: formData.get(`${section}.${item}.email`) === 'on',
    app: formData.get(`${section}.${item}.app`) === 'on',
  })
  return {
    phoneCalls: {
      enabled: sectionStates.phoneCalls,
      canceledAppointment: channel('phoneCalls', 'canceledAppointment'),
      allUnscheduledCalls: channel('phoneCalls', 'allUnscheduledCalls'),
      newPatientUnscheduledCalls: channel('phoneCalls', 'newPatientUnscheduledCalls'),
      callbackRequested: channel('phoneCalls', 'callbackRequested'),
      missedCalls: channel('phoneCalls', 'missedCalls'),
      voicemail: channel('phoneCalls', 'voicemail'),
    },
    aiAgent: {
      enabled: sectionStates.aiAgent,
      newAIAgentCall: channel('aiAgent', 'newAIAgentCall'),
      emergencyAppointmentRequest: channel('aiAgent', 'emergencyAppointmentRequest'),
      newAppointmentBooked: channel('aiAgent', 'newAppointmentBooked'),
    },
    textMessages: {
      enabled: sectionStates.textMessages,
      newTextMessage: channel('textMessages', 'newTextMessage'),
    },
    formSubmissions: {
      enabled: sectionStates.formSubmissions,
      newFormSubmission: channel('formSubmissions', 'newFormSubmission'),
    },
    scheduler: {
      enabled: sectionStates.scheduler,
      newAppointmentBooked: channel('scheduler', 'newAppointmentBooked'),
    },
    pmsData: {
      enabled: sectionStates.pmsData,
      noPatientLinkedOnScheduledContact: channel('pmsData', 'noPatientLinkedOnScheduledContact'),
      noAppointmentFoundOnScheduledContact: channel('pmsData', 'noAppointmentFoundOnScheduledContact'),
      brokenAppointment: channel('pmsData', 'brokenAppointment'),
    },
    workflowPaused: { enabled: sectionStates.workflowPaused, ...channel('workflowPaused', 'workflowPaused') },
  }
}

// The API only writes this alert's on-by-default value for users created after it shipped, so a
// user with nothing stored is shown it on, and saving the page stores that.
function withWorkflowPausedDefaults(stored: StoredNotificationSettings): NotificationSettings {
  const { workflowPaused, ...rest } = stored
  return {
    ...rest,
    workflowPaused: {
      enabled: workflowPaused?.enabled ?? true,
      workflowPaused: { email: workflowPaused?.email ?? true, app: workflowPaused?.app ?? true },
    },
  }
}

export type UseUserNotificationsReturn = {
  notificationSettings: NotificationSettings | undefined
  loading: boolean
  /** A super admin on their own page: only Agent Diagnostics and live alerts apply. */
  canEditAgentDiagnostics: boolean
  agentDiagnostics: AgentDiagnosticsSettings | undefined
  alertEmail: string | undefined
  aiCallDiagnosticsSectionEnabled: boolean
  setAiCallDiagnosticsSectionEnabled: (v: boolean) => void
  agentChangesSectionEnabled: boolean
  setAgentChangesSectionEnabled: (v: boolean) => void
  isDirtyForm: boolean
  setIsDirtyForm: (v: boolean) => void
  phoneCallSectionEnabled: boolean
  setPhoneCallSectionEnabled: (v: boolean) => void
  AIAgentSectionEnabled: boolean
  setAIAgentSectionEnabled: (v: boolean) => void
  textMessageSectionEnabled: boolean
  setTextMessageSectionEnabled: (v: boolean) => void
  formSubmissionSectionEnabled: boolean
  setFormSubmissionSectionEnabled: (v: boolean) => void
  schedulerSectionEnabled: boolean
  setSchedulerSectionEnabled: (v: boolean) => void
  pmsDataSectionEnabled: boolean
  setPmsDataSectionEnabled: (v: boolean) => void
  workflowPausedSectionEnabled: boolean
  setWorkflowPausedSectionEnabled: (v: boolean) => void
  liveAlerts: LiveAlertSettings
  setLiveAlerts: (patch: Partial<LiveAlertPreferences>) => void
  formRef: React.RefObject<HTMLFormElement | null>
  onSave: () => void
}

export function useUserNotifications(): UseUserNotificationsReturn {
  const { userId } = useParams()
  const me = useUser()
  const canEditAgentDiagnostics = me?.type === `super` && me.id === userId
  const [agentDiagnostics, setAgentDiagnostics] = useState<AgentDiagnosticsSettings>()
  const [aiCallDiagnosticsSectionEnabled, setAiCallDiagnosticsSectionEnabled] = useState(false)
  const [agentChangesSectionEnabled, setAgentChangesSectionEnabled] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>()

  const [isDirtyForm, setIsDirtyForm] = useState(false)
  const [phoneCallSectionEnabled, setPhoneCallSectionEnabled] = useState(false)
  const [aiAgentSectionEnabled, setAiAgentSectionEnabled] = useState(false)
  const [textMessageSectionEnabled, setTextMessageSectionEnabled] = useState(false)
  const [formSubmissionSectionEnabled, setFormSubmissionSectionEnabled] = useState(false)
  const [schedulerSectionEnabled, setSchedulerSectionEnabled] = useState(false)
  const [pmsDataSectionEnabled, setPmsDataSectionEnabled] = useState(false)
  const [workflowPausedSectionEnabled, setWorkflowPausedSectionEnabled] = useState(true)

  const [liveAlertEdits, setLiveAlertEdits] = useState<LiveAlertPreferences>()
  const [{ data: liveAlertData, fetching: liveAlertsFetching, error: liveAlertsError }, reexecuteLiveAlerts] = useQuery(
    {
      query: LIVE_ALERT_PREFERENCES_QUERY,
      variables: { userId: userId ?? '' },
      pause: !userId,
    },
  )
  const storedLiveAlerts: LiveAlertPreferences | undefined = liveAlertData?.userNotificationSettings
    ? {
        newTextMessage: liveAlertData.userNotificationSettings.textMessages?.newTextMessage
          ? {
              popup: liveAlertData.userNotificationSettings.textMessages.newTextMessage.popup ?? undefined,
              sound: liveAlertData.userNotificationSettings.textMessages.newTextMessage.sound ?? undefined,
            }
          : undefined,
        incomingCall: liveAlertData.userNotificationSettings.phoneCalls?.incomingCall
          ? {
              popup: liveAlertData.userNotificationSettings.phoneCalls.incomingCall.popup ?? undefined,
              sound: liveAlertData.userNotificationSettings.phoneCalls.incomingCall.sound ?? undefined,
            }
          : undefined,
      }
    : undefined
  // Resolved at render rather than seeded into state: the setting arrives after
  // mount, and a `useState` initializer would pin the switches to the defaults
  // it happened to see first.
  const liveAlerts = withLiveAlertDefaults(liveAlertEdits ?? storedLiveAlerts)
  const liveAlertsReady = !liveAlertsFetching && (!!liveAlertData || !!liveAlertsError)

  const setLiveAlerts = (patch: Partial<LiveAlertPreferences>) => {
    const next = { ...liveAlerts, ...patch }
    if (`enabled` in patch) {
      if (patch.enabled === false) {
        next.newTextMessage = { ...next.newTextMessage, popup: false, sound: false }
        next.incomingCall = { ...next.incomingCall, popup: false, sound: false }
      } else if (patch.enabled === true) {
        next.newTextMessage = { ...next.newTextMessage, popup: true }
        next.incomingCall = { ...next.incomingCall, popup: true }
      }
    }
    next.enabled = !!(next.newTextMessage.popup || next.incomingCall.popup)
    setLiveAlertEdits(next)
    setIsDirtyForm(true)
  }

  useEffect(() => {
    if (!userId) return
    setLoading(true)
    if (canEditAgentDiagnostics) {
      gqlQuery(SUPER_NOTIFICATIONS_QUERY, {}, { client: opsClient })
        .then((result) => {
          const settings = result.data?.getSuperNotifications
          setAgentDiagnostics(settings)
          setAiCallDiagnosticsSectionEnabled(settings?.enabled ?? false)
          setAgentChangesSectionEnabled(settings?.agentChangesEnabled ?? false)
        })
        .catch((err) => logError(`Error fetching agent diagnostics settings`, err))
        .finally(() => setLoading(false))
      return
    }
    gqlQuery<{ userNotificationSettings: StoredNotificationSettings }>(USER_NOTIFICATION_SETTINGS_QUERY, { userId })
      .then((result) => {
        const stored = result.data?.userNotificationSettings
        if (stored) {
          const ns = withWorkflowPausedDefaults(stored)
          setNotificationSettings(ns)
          setWorkflowPausedSectionEnabled(ns.workflowPaused?.enabled ?? true)
          setPhoneCallSectionEnabled(ns?.phoneCalls?.enabled ?? false)
          setAiAgentSectionEnabled(ns?.aiAgent?.enabled ?? false)
          setTextMessageSectionEnabled(ns?.textMessages?.enabled ?? false)
          setFormSubmissionSectionEnabled(ns?.formSubmissions?.enabled ?? false)
          setSchedulerSectionEnabled(ns?.scheduler?.enabled ?? false)
          setPmsDataSectionEnabled(ns?.pmsData?.enabled ?? false)
        }
      })
      .catch((err) => logError(`Error fetching notification settings`, err))
      .finally(() => setLoading(false))
  }, [userId, canEditAgentDiagnostics])

  useEffect(() => {
    if (liveAlertsReady) return
    setLoading(true)
  }, [liveAlertsReady])

  const onSave = async () => {
    if (!liveAlertsReady || !userId) return
    setSaving(true)
    try {
      const form = formRef.current
      if (!form) {
        logError(`Notification form ref is null — cannot save`)
        notifyError(`Failed to save notification settings`)
        return
      }
      const formData = new FormData(form)

      if (canEditAgentDiagnostics) {
        const saved = await gqlMutation(
          UPDATE_SUPER_NOTIFICATIONS_MUTATION,
          {
            input: {
              enabled: aiCallDiagnosticsSectionEnabled,
              toolError: formData.get(`aiCallDiagnostics.toolError.email`) === `on`,
              toolTimeout: formData.get(`aiCallDiagnostics.toolTimeout.email`) === `on`,
              toolDuplicate: formData.get(`aiCallDiagnostics.toolDuplicate.email`) === `on`,
              noCallsIn48h: formData.get(`aiCallDiagnostics.noCallsIn48h.email`) === `on`,
              agentChangesEnabled: agentChangesSectionEnabled,
              agentChangeOffers: formData.get(`aiAgentChanges.offers.email`) === `on`,
              agentChangeServices: formData.get(`aiAgentChanges.services.email`) === `on`,
              agentChangeBusinessDetails: formData.get(`aiAgentChanges.businessDetails.email`) === `on`,
              agentChangeAppointmentTypes: formData.get(`aiAgentChanges.appointmentTypes.email`) === `on`,
              agentChangeInsurance: formData.get(`aiAgentChanges.insurance.email`) === `on`,
            },
          },
          { client: opsClient },
        )
        if (saved.error || !saved.data?.updateSuperNotifications) {
          logError(`Error saving agent diagnostics settings`, saved.error)
          notifyError(`Failed to save notification settings`)
          return
        }
        setAgentDiagnostics(saved.data.updateSuperNotifications)
      }

      const input = buildNotificationInput(formData, {
        phoneCalls: phoneCallSectionEnabled,
        aiAgent: aiAgentSectionEnabled,
        textMessages: textMessageSectionEnabled,
        formSubmissions: formSubmissionSectionEnabled,
        scheduler: schedulerSectionEnabled,
        pmsData: pmsDataSectionEnabled,
        workflowPaused: workflowPausedSectionEnabled,
      })

      input.textMessages = {
        ...input.textMessages,
        newTextMessage: {
          ...input.textMessages?.newTextMessage,
          popup: liveAlerts.newTextMessage.popup,
          sound: liveAlerts.newTextMessage.sound,
        },
      }
      input.phoneCalls = {
        ...input.phoneCalls,
        incomingCall: {
          ...input.phoneCalls?.incomingCall,
          popup: liveAlerts.incomingCall.popup,
          sound: liveAlerts.incomingCall.sound,
        },
      }

      const result = await gqlMutation(UPDATE_USER_NOTIFICATION_SETTINGS_MUTATION, {
        userId,
        input,
      })

      if (result.error || !result.data?.updateUserNotificationSettings?.success) {
        logError(`Error saving notification settings`, result.error)
        notifyError(`Failed to save notification settings`)
        return
      }

      reexecuteLiveAlerts({ requestPolicy: 'network-only' })

      notifySuccess(`Notification settings saved successfully`)
      setIsDirtyForm(false)
    } catch (err) {
      logError(`Error saving notification settings`, err)
      notifyError(`Failed to save notification settings`)
    } finally {
      setSaving(false)
    }
  }

  const formRef = useRef<HTMLFormElement>(null)

  return {
    notificationSettings,
    // Super-admin mode comes from `me`, so wait for it rather than flash the workspace sections.
    loading: loading || !me?.id,
    canEditAgentDiagnostics,
    agentDiagnostics,
    alertEmail: canEditAgentDiagnostics ? (me?.email ?? undefined) : undefined,
    aiCallDiagnosticsSectionEnabled,
    setAiCallDiagnosticsSectionEnabled,
    agentChangesSectionEnabled,
    setAgentChangesSectionEnabled,
    isDirtyForm,
    setIsDirtyForm,
    phoneCallSectionEnabled,
    setPhoneCallSectionEnabled,
    AIAgentSectionEnabled: aiAgentSectionEnabled,
    setAIAgentSectionEnabled: setAiAgentSectionEnabled,
    textMessageSectionEnabled,
    setTextMessageSectionEnabled,
    formSubmissionSectionEnabled,
    setFormSubmissionSectionEnabled,
    schedulerSectionEnabled,
    setSchedulerSectionEnabled,
    pmsDataSectionEnabled,
    setPmsDataSectionEnabled,
    workflowPausedSectionEnabled,
    setWorkflowPausedSectionEnabled,
    liveAlerts,
    setLiveAlerts,
    formRef,
    onSave,
  }
}
